import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import gettext from '@sdk/lang/gettext';
import useModalState, { ModalState } from '@sdk/components/ui/modal/useModalState';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { TaskTemplateFormValues } from '@web/shiftTasks/ComposeTask/types/TaskTemplateFormValues';
import { getTaskTemplatePayload, mapTaskTemplateToFormValues } from '@web/shiftTasks/utils/mappers';
import { TaskTemplate } from '@web/shiftTasks/types/TaskTemplate';
import { useEditTaskTemplate } from '@web/shiftTasks/hooks/useEditTaskTemplate';

export function useEditTaskTemplateForm(modalState: ModalState, originalTemplate: TaskTemplate) {
  const editTaskTemplateMutation = useEditTaskTemplate(originalTemplate.id);
  const taskTemplateFormValues = useMemo(
    () => (originalTemplate ? mapTaskTemplateToFormValues(originalTemplate) : null),
    [originalTemplate]
  );
  const formMethods = useForm<TaskTemplateFormValues>({
    defaultValues: taskTemplateFormValues,
  });
  const { formState, getValues, handleSubmit } = formMethods;
  const { showErrorToast } = useToast();
  const taskEditingOptionsModalState = useModalState();

  const { close } = modalState;

  // opens prompt whether to update future task instances
  const onSubmit = handleSubmit(() => {
    taskEditingOptionsModalState.open();
  });

  // actually saving changes
  const saveTaskTemplate = async (shouldUpdateTaskInstances: boolean) => {
    try {
      if (formState.isDirty) {
        const formValues = getValues();
        const payload = getTaskTemplatePayload(formValues);
        await editTaskTemplateMutation.mutateAsync({ payload, shouldUpdateTaskInstances });
      }

      close();
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      taskEditingOptionsModalState.close();
    }
  };

  return {
    formMethods,
    onSubmit,
    saveTaskTemplate,
    taskEditingOptionsModalState,
    title: gettext('Edit task template'),
  };
}
